import React, { FC, Suspense, useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay/hooks';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AIStudioDialog } from 'src/components/pages/aiStudio/aiStudioDialog';
import ProductCatalogDialog from 'src/components/pages/aiStudio/components/dialogs/productCatalogDialog';
import { ProductSideImage } from 'src/components/pages/aiStudio/types';
import { ProductColor } from 'src/components/pages/productConfiguration/state/model/modelColor';
import ConnectModelDialog from 'src/components/pages/productConfiguration/steps/6-done/connectModelDialog';
import DuplicateDialog from 'src/components/pages/productConfiguration/steps/6-done/duplicateDialog';
import { productListConnectModelMutation$data } from 'src/components/pages/products/__generated__/productListConnectModelMutation.graphql';
import MarketplacePreviewUrlDialog from 'src/components/pages/products/marketplacePreviewUrlDialog';
import { DialogOverlayConnect } from 'src/components/pages/products/productList';
import AddSalesChannelDialog from 'src/components/pages/salesChannels/addSalesChannelDialog';
import { SalesChannelNotAvailableDialogWindow } from 'src/components/pages/salesChannels/salesChannel';
import ChangeProductIcon from 'src/components/pages/userInterfaceSettings/icons/change-product.svg';
import DesktopWideIcon from 'src/components/pages/userInterfaceSettings/icons/desktop-wide.svg';
import DesktopIcon from 'src/components/pages/userInterfaceSettings/icons/desktop.svg';
import MobileIcon from 'src/components/pages/userInterfaceSettings/icons/mobile.svg';
import type { LivePreviewSize } from 'src/components/pages/userInterfaceSettings/livePreview/livePreview';
import mobileIphone380x844Skin from 'src/components/pages/userInterfaceSettings/livePreview/skins/mobile-iphone380x844.png';
import { Badge } from 'src/components/widgets/badge';
import { useRelayStore } from 'src/relay';
import config from 'src/shared/config';
import { NoIntegrationDialog, useUpgradeDialogs } from 'src/shared/dialogs';
import { useFeatureFlag } from 'src/shared/feature-flags';
import { ButtonActionsDelete, Center } from 'src/shared/globals';
import { T, backofficeUrl, stringFormat, toLocalID, uuid } from 'src/shared/helpers';
import usePermissions from 'src/shared/hooks/usePermissions';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import VerticalThreePoint from 'src/shared/icons/ellipsis-v-solid.svg';
import ExternalLinkIcon from 'src/shared/icons/external-link-alt-solid.svg';
import CloseIcon from 'src/shared/icons/times-solid.svg';
import theme from 'src/shared/theme';
import { isComposerAddToCartMessage, isCustomizerAddToCartMessage } from 'src/shared/zakekeMessage';
import styled, { css } from 'styled-components';
import { ButtonActions, Link, SelectActions } from '../buttons';
import { MessageDialog, QuestionDialog, useDialogManager } from '../dialog';
import { Icon } from '../icon';
import Loader from '../loader';
import { NotificationBanner } from '../notificationBanner';
import {
	removeNotification,
	showErrorNotification,
	showInfoNotification,
	showNotification,
	showSuccessNotification,
	showWarningNotification
} from '../notificationToaster';
import UpgradeDialog from '../upgradeDialog/upgradeDialog';
import WaitForProductExists from '../waitForProductExists';
import { ZSwitch } from '../zswitch';
import { ZTooltip } from '../ztooltip';
import { testPageConnectModelMutation, testPageConnectModelMutation$data } from './__generated__/testPageConnectModelMutation.graphql';
import { testPageDeleteMutation } from './__generated__/testPageDeleteMutation.graphql';
import { testPageDuplicateMutation, testPageDuplicateMutation$data } from './__generated__/testPageDuplicateMutation.graphql';
import { testPageOrderMutation } from './__generated__/testPageOrderMutation.graphql';
import { testPageProductSetStatusMutation } from './__generated__/testPageProductSetStatusMutation.graphql';
import { testPageQuery } from './__generated__/testPageQuery.graphql';
import { DiscoveryModePanel } from './DiscoveryModePanel';

const FullscreenOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #f5f7fa;
    z-index: 20;
    display: flex;
    flex-direction: column;
`;

const TopBar = styled.div<{ $discovery?: boolean }>`
    position: relative;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding: 12px 24px;
    background: ${(props) => (props.$discovery ? 'linear-gradient(135deg, #F46200 0%, #E6167F 100%)' : 'white')};
    border-bottom: 1px solid ${(props) => (props.$discovery ? '#E6167F' : '#e2e8f0')};
    min-height: 64px;
    transition: border-color 0.25s ease;
`;

const TopBarLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const TopBarCenter = styled.div`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    gap: 4px;
`;

const TopBarRight = styled.div`
    position: absolute;
    right: 24px;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const CloseButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: #000000;
    color: #ffffff;
    cursor: pointer;
    transition: background 0.2s ease;
    flex-shrink: 0;

    &:hover {
        background: #333333;
    }

    ${Icon} {
        width: 16px;
        height: 16px;
        min-width: 0;
        fill: currentColor;
    }
`;

const ChangeProductButton = styled.button<{ $discovery?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    border-radius: 9999px;
    background: ${(props) => (props.$discovery ? 'rgba(255, 255, 255, 0.15)' : '#f3f5f6')};
    color: ${(props) => (props.$discovery ? '#ffffff' : '#000000')};
    cursor: pointer;
    transition:
        background 0.2s ease,
        color 0.2s ease;

    &:hover {
        background: ${(props) => (props.$discovery ? 'rgba(255, 255, 255, 0.25)' : 'black')};
        color: white;
    }

    ${Icon} {
        width: 22px;
        height: 22px;
        fill: currentColor;
        min-width: 0;
        min-height: 0;
    }
`;

const MenuDivider = styled.hr`
    width: 100%;
    border: none;
    border-top: 1px solid #e2e8f0;
    margin: 4px 0;
`;

const ProductName = styled.h3<{ $discovery?: boolean }>`
    font-size: 18px;
    font-weight: 600;
    color: ${(props) => (props.$discovery ? '#ffffff' : '#1e293b')};
    margin: 0;
`;

const StyledBadge = styled(Badge)`
    margin-left: 0;
`;

const BadgeContainer = styled.div`
    min-width: 110px;
`;

const BannerSection = styled.div`
    flex-shrink: 0;
    padding: 12px 24px 0;
`;

const PublishSwitchWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: -20px;
`;

const PublishSwitchLabel = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: #1e293b;
`;

const BottomBar = styled.div`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 10px 24px;
    background: white;
    border-top: 1px solid #e2e8f0;
`;

const ModeToggleContainer = styled.div`
    display: inline-flex;
    flex-shrink: 0;
    background: #e2e8f0;
    border-radius: 8px;
    padding: 3px;
    gap: 2px;
`;

const ModeToggleButton = styled.button<{ $active: boolean; $variant?: 'test' | 'discovery' }>`
    padding: 6px 20px;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    background: ${(props) => {
        if (!props.$active) return 'transparent';
        return props.$variant === 'discovery' ? 'linear-gradient(135deg, #F46200 0%, #E6167F 100%)' : 'white';
    }};
    color: ${(props) => {
        if (!props.$active) return '#64748b';
        return props.$variant === 'discovery' ? '#ffffff' : '#1e293b';
    }};
    box-shadow: ${(props) => (props.$active ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none')};

    &:hover {
        color: ${(props) => {
            if (!props.$active) return '#334155';
            return props.$variant === 'discovery' ? '#ffffff' : '#1e293b';
        }};
    }
`;

const UISettingsButton = styled.button`
    padding: 6px 14px;
    border: 1.5px solid rgba(255, 255, 255, 0.4);
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.15);
    color: #ffffff;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        background: rgba(255, 255, 255, 0.25);
        border-color: rgba(255, 255, 255, 0.6);
    }
`;

const IframeSection = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: 20px 24px;
    overflow: auto;
`;

const IframeContainer = styled.div`
    flex: 1;
    position: relative;
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-radius: 12px;
    overflow: hidden;
    min-height: 620px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const StyledIframe = styled.iframe`
    width: 100%;
    height: 100%;
    min-height: 620px;
    border: 0;
`;

type SkinOptions = {
    url: string;
    gutterTop: number;
    gutterBottom: number;
    gutterLeft: number;
    gutterRight: number;
};

const previewSizeConfigs: Record<LivePreviewSize, { width: number; height: number; skin: SkinOptions | null }> = {
    'desktop-wide': {
        width: window.innerWidth,
        height: window.innerHeight,
        skin: null
    },
    desktop: {
        width: 1220,
        height: 674,
        skin: null
    },
    mobile: {
        width: 380,
        height: 844,
        skin: {
            url: mobileIphone380x844Skin,
            gutterTop: 100,
            gutterBottom: 84,
            gutterLeft: 16,
            gutterRight: 16
        }
    }
};

const PreviewSkin = styled.div<{
    $width: number;
    $height: number;
    $scale: number;
    $skin?: SkinOptions | null;
}>`
    margin: auto;
    background: transparent;
    width: ${(props) => props.$width}px;
    height: ${(props) => props.$height}px;
    transform: scale(${(props) => props.$scale});
    transform-origin: center center;
    position: absolute;
    overflow: hidden;

    ${(props) =>
        !props.$skin &&
        css`
            border-radius: 16px;
            box-shadow:
                rgb(0, 0, 0, 0.15) 0 4px 20px 0,
                rgb(0, 0, 0, 0.15) 0 0 3px 0;
        `}

    ${(props) =>
        props.$skin &&
        css`
            padding-top: ${props.$skin.gutterTop}px;
            padding-bottom: ${props.$skin.gutterBottom}px;
            padding-left: ${props.$skin.gutterLeft}px;
            padding-right: ${props.$skin.gutterRight}px;

            &:after {
                content: ' ';
                position: absolute;
                inset: 0;
                background: url('${props.$skin.url}');
                z-index: 1;
                pointer-events: none;
                user-select: none;
            }
        `}
`;

const PreviewSizeButton = styled.button<{ $active?: boolean; $discovery?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    border-radius: 9999px;
    background: ${(props) => (props.$discovery ? 'rgba(255, 255, 255, 0.15)' : '#f3f5f6')};
    color: ${(props) => (props.$active ? (props.$discovery ? '#ffffff' : '#000000') : props.$discovery ? 'rgba(255, 255, 255, 0.4)' : '#94a3b8')};
    cursor: pointer;
    transition:
        background 0.2s ease,
        color 0.2s ease;

    &:hover {
        background: ${(props) => (props.$discovery ? 'rgba(255, 255, 255, 0.25)' : 'black')};
        color: white;
    }

    ${Icon} {
        width: 22px;
        height: 22px;
        fill: currentColor;
        min-width: 0;
        min-height: 0;
    }
`;

const usePreviewSkinConfig = (containerRef: React.RefObject<HTMLDivElement>, size: LivePreviewSize) => {
    const [config, setConfig] = useState({ width: 0, height: 0, scale: 1, skin: null as SkinOptions | null });
    const updateConfig = useCallback(
        (size: LivePreviewSize) => {
            if (!containerRef.current) return;
            const { width, height, skin } = previewSizeConfigs[size];
            const scale = Math.min(1, (containerRef.current.clientWidth - 12) / width, (containerRef.current.clientHeight - 12) / height);
            setConfig({ width, height, scale, skin });
        },
        [size]
    );
    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => updateConfig(size));
        if (containerRef.current) resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, [size]);
    useEffect(() => updateConfig(size), [size]);
    return config;
};

type PreviewModel = testPageQuery['response']['model'];

const TestPage: FC<{ productId: string; showCopyUrl?: boolean; isClassic?: boolean }> = ({ productId, showCopyUrl, isClassic }) => {
    // History
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { showPlanUpgradeDialog } = useUpgradeDialogs();
    const { showDialog, closeDialog } = useDialogManager();
    const { isAllowed } = usePermissions();
    const [incrementStoreKey] = useRelayStore((state) => [state.incrementRelayStoreKey]);
    const { sellerMarketplace } = useSubscriptionData();
    const isDiscoveryModeEnabled = useFeatureFlag('discoveryMode');
    const [hasResumed, setHasResumed] = useState(false);
    const [activeProductId, setActiveProductId] = useState(productId);

    const [fetchKey, setFetchKey] = useState(0);
    const [isPending, startTransition] = useTransition();
    const [discoveryMode, setDiscoveryMode] = useState(searchParams.get('mode') === 'discovery');
    const [isInfoBannerVisible, setIsInfoBannerVisible] = useState(true);
    const [previewSize, setPreviewSize] = useState<LivePreviewSize>('desktop-wide');
    const previewContainerRef = useRef<HTMLDivElement>(null);
    const skinConfig = usePreviewSkinConfig(previewContainerRef, previewSize);
    const [discoverySelections, setDiscoverySelections] = useState<Record<string, string | null>>(() => ({
        printing: searchParams.get('printing') ?? null,
        pricing: searchParams.get('pricing') ?? null,
        ai: searchParams.get('ai') ?? null,
        tools: searchParams.get('tools') ?? null
    }));

    const handleDiscoverySelect = useCallback((category: string, id: string | null) => {
        setDiscoverySelections((prev) => ({ ...prev, [category]: id }));
    }, []);

    // Sync discovery state to URL search params so browser back restores it
    useEffect(() => {
        const params = new URLSearchParams(searchParams.toString());
        let changed = false;

        // mode
        if (discoveryMode) {
            if (params.get('mode') !== 'discovery') {
                params.set('mode', 'discovery');
                changed = true;
            }
        } else {
            if (params.has('mode')) {
                params.delete('mode');
                changed = true;
            }
        }

        // category selections
        for (const [key, value] of Object.entries(discoverySelections)) {
            if (value) {
                if (params.get(key) !== value) {
                    params.set(key, value);
                    changed = true;
                }
            } else {
                if (params.has(key)) {
                    params.delete(key);
                    changed = true;
                }
            }
        }

        if (changed) {
            const newSearch = params.toString();
            const newPath = `${location.pathname}${newSearch ? '?' + newSearch : ''}`;
            navigate(newPath, { replace: true });
        }
    }, [discoveryMode, discoverySelections]);

    // Queries
    const { model, subscription, salesChannels, shopifyAuthEcommerceSettings, sellerUserInterfaces } = useLazyLoadQuery<testPageQuery>(
        graphql`
            query testPageQuery($productId: ID!) {
                model(id: $productId) {
                    id
                    name
                    code
                    provider
                    statusID
                    printTypes {
                        printTypeID
                    }
                    colors {
                        colorID
                        name
                        code
                        colorCode
                    }
                    iframeUrl {
                        testUrl
                        copyUrl
                    }
                    salesChannel {
                        id
                        ecommerceSettings {
                            ecommerceName
                            remoteUrl
                            productsApi
                            hasProductsApi
                            isMarketplace
                            definitiveUrl
                        }
                    }
                    zakekeEnvironment
                    canAddAdditionalCode
                    additionalCodes {
                        code
                        salesChannelID
                        merchantProductName
                        salesChannel {
                            id
                            ecommerceSettings {
                                ecommerceName
                                remoteUrl
                                definitiveUrl
                            }
                        }
                    }
                    modelColorMappings {
                        colorID
                        salesChannelID
                        merchantCode
                    }
                }
                subscription {
                    maxProducts {
                        pricingVersion
                        numPublishedProducts
                        maxProducts
                        numProducts
                    }
                    isOverMaxNumTestOrders
                }
                salesChannels(status: ACTIVE) {
                    id
                    ecommerceSettings {
                        ecommerceID
                        ecommerceName
                        isMarketplace
                        hasProductsApi
                        remoteUrl
                        definitiveUrl
                    }
                    detail {
                        id
                        properties {
                            name
                        }
                    }
                }
                shopifyAuthEcommerceSettings {
                    isTestStore
                }
                sellerUserInterfaces(first: 1) {
                    nodes {
                        isDefault
                        userInterfaceId
                    }
                }
            }
        `,
        { productId: activeProductId },
        { fetchKey, fetchPolicy: 'network-only' }
    );

    if (!model) {
        throw new Error(`Oops: product not found!`);
    }

    const isPublished = model.statusID === 1;

    const isOldUserInterface = useMemo(() => {
        const defaultUI = sellerUserInterfaces?.nodes?.find((ui) => ui?.isDefault);
        return defaultUI?.userInterfaceId === 1;
    }, [sellerUserInterfaces]);

    useEffect(() => {
        if (hasResumed) return;

        const { doPublishImmediately, productID } = location.state || {};
        if (!doPublishImmediately || !productID || !model) return;

        handleSetStatusClick(model);

        setHasResumed(true);
    }, [hasResumed, location.state, model, navigate]);

    useEffect(() => {
        const shouldPublish = searchParams.get('publish') === 'true';

        if (shouldPublish) {
            handleSetStatusClick(model);
        }
    }, [searchParams, model]);

    // Mutations
    const [commitTestOrder] = useMutation<testPageOrderMutation>(graphql`
        mutation testPageOrderMutation($designDocID: String, $compositionDocID: String) {
            createTestOrder(designDocID: $designDocID, compositionDocID: $compositionDocID) {
                orderID
            }
        }
    `);

    const [setStatusProduct] = useMutation<testPageProductSetStatusMutation>(graphql`
        mutation testPageProductSetStatusMutation($input: SetStatusProductInput!) {
            setStatusProduct(input: $input) {
                model {
                    id
                    statusID
                }
                errors {
                    ... on Error {
                        message
                    }
                }
            }
        }
    `);

    const [commitDuplicate, isInFightDuplicate] = useMutation<testPageDuplicateMutation>(graphql`
        mutation testPageDuplicateMutation($id: ID!, $targets: [ModelDuplicateTargetInput!]!) {
            duplicateModel(id: $id, targets: $targets) {
                productIDs
                resultMessage
                status
            }
        }
    `);

    const [commitConnectModel, isInFightConnectModel] = useMutation<testPageConnectModelMutation>(graphql`
        mutation testPageConnectModelMutation($id: ID!, $target: ModelDuplicateTargetInput!) {
            connectModel(id: $id, target: $target)
        }
    `);

    const [deleteProduct, isInFightDelete] = useMutation<testPageDeleteMutation>(graphql`
        mutation testPageDeleteMutation($productID: String!) {
            deleteProduct(productID: $productID) {
                id
            }
        }
    `);

    // Hooks
    const onMessageReceivedFromIframe = useCallback(
        (event: MessageEvent) => {
            const data = event.data;

            if (subscription?.isOverMaxNumTestOrders) {
                return;
            }

            // Composer add-to-cart: { zakekeMessageType: "AddToCart", message: { composition, preview, quantity } }
            if (isComposerAddToCartMessage(data)) {
                commitTestOrder({
                    variables: {
                        compositionDocID: data.message.composition
                    },
                    onCompleted: ({ createTestOrder }) => {
                        navigate(backofficeUrl(`/order/${createTestOrder?.orderID}`));
                    }
                });
                return;
            }

            // Customizer add-to-cart: { zakekeMessageType: 0, designId: "..." }
            if (isCustomizerAddToCartMessage(data)) {
                commitTestOrder({
                    variables: {
                        designDocID: data.designId
                    },
                    onCompleted: ({ createTestOrder }) => {
                        navigate(backofficeUrl(`/order/${createTestOrder?.orderID}`));
                    }
                });
            }
        },
        [commitTestOrder]
    );

    useEffect(() => {
        window.addEventListener('message', onMessageReceivedFromIframe);
        return () => window.removeEventListener('message', onMessageReceivedFromIframe);
    }, [onMessageReceivedFromIframe]);

    // Listen for OPEN_AI_STUDIO postMessage from the customizer iframe
    useEffect(() => {
        const handleOpenAIStudio = (event: MessageEvent) => {
            if (event.data?.type === 'OPEN_AI_STUDIO') {
                const aiProductId = parseInt(event.data?.params?.productId, 10);
                const variantId = event.data?.params?.variantId ? parseInt(event.data?.params?.variantId, 10) : undefined;
                const docId = event.data?.params?.docId || undefined;
                const sidesData = event.data?.params?.sides;

                // Convert sides data to ProductSideImage array
                let sideImages: ProductSideImage[] | undefined;
                if (sidesData && sidesData.length > 0) {
                    sideImages = sidesData.map((side: { sideId: number; sideName: string; preview: string }) => ({
                        sideId: side.sideId,
                        sideName: side.sideName,
                        url: side.preview
                    }));
                }

                if (aiProductId) {
                    showDialog(
                        'ai-studio-dialog',
                        <AIStudioDialog
                            productId={aiProductId}
                            variantId={variantId}
                            sideImages={sideImages}
                            docId={docId}
                            onClose={() => closeDialog('ai-studio-dialog')}
                        />
                    );
                }
            }
        };

        window.addEventListener('message', handleOpenAIStudio);
        return () => window.removeEventListener('message', handleOpenAIStudio);
    }, [productId, showDialog, closeDialog]);

    const handleSetStatusClick = (product: PreviewModel) => {
        const pricingVersion = subscription?.maxProducts.pricingVersion ?? 'VERSION_1';
        if (pricingVersion === 'VERSION_2' && product!.statusID === 2) {
            const numCurrentPublishedProducts = subscription?.maxProducts.numPublishedProducts ?? 0;
            const numMaxPublishedProducts = subscription?.maxProducts.maxProducts ?? 0;

            if (!salesChannels.some((s) => s.ecommerceSettings?.ecommerceName !== 'NONE')) {
                showDialog(
                    'complete-ecommerce-integration',
                    <NoIntegrationDialog
                        redirectPage={removeCultureAdmin()}
                        productId={toLocalID(productId) ?? 0}
                        subTitleNoIntegration={T._('Ready to start selling customizable products?', 'Admin')}
                        message={
                            <label>
                                {T._('You need to integrate Zakeke with your store in order to publish and start selling your customizable products.', 'Admin')}
                            </label>
                        }
                    ></NoIntegrationDialog>
                );
                return;
            } else if (numCurrentPublishedProducts >= numMaxPublishedProducts) {
                showDialog(
                    'Upgrade_Plan',
                    <UpgradeDialog
                        redirectUrlFromDashboard={encodeURI(window.location.href)}
                        marketPlace={sellerMarketplace ?? undefined}
                        message={
                            <>
                                {stringFormat(
                                    T._(
                                        `Your current plan allows for {0} published products, and you've reached your limit! To publish this product, upgrade your plan to increase your published product slots.`,
                                        'Admin'
                                    ),
                                    numMaxPublishedProducts.toString()
                                )}
                            </>
                        }
                        onUpgradeCompleted={async () => {
                            console.log('upgrade completato, continuo con la pubblicazione');
                            if (!showCopyUrl) {
                                showInfoNotification({
                                    message: product!.statusID === 1 ? T._('Unpublishing product', 'Admin') : T._('Publishing product', 'Admin')
                                });
                            }
                            setStatusProduct({
                                variables: {
                                    input: {
                                        productID: product!.id!,
                                        status: product!.statusID === 1 ? 'UN_PUBLISHED' : 'PUBLISHED'
                                    }
                                },
                                onError: () => {
                                    showErrorNotification({
                                        message: product!.statusID === 1 ? T._('Failed Unpublish product', 'Admin') : T._('Failed Publish product', 'Admin')
                                    });
                                },
                                onCompleted: (response, errors) => {
                                    if (!showCopyUrl) {
                                        showSuccessNotification({
                                            message: product!.statusID === 1 ? T._('Product unpublished', 'Admin') : T._('Product published', 'Admin')
                                        });
                                    }
                                }
                            });
                        }}
                    />
                );

                return;
            }
        }

        if (
            product?.salesChannel.ecommerceSettings?.ecommerceName === 'NONE' &&
            salesChannels.some((s) => s.ecommerceSettings?.ecommerceName !== 'NONE') &&
            product.additionalCodes.length == 0 &&
            product.modelColorMappings.length == 0 &&
            product?.statusID === 2
        ) {
            const realSalesChannels = salesChannels.filter((s) => s.ecommerceSettings?.ecommerceName !== 'NONE');
            showDialog(
                'connectProductDialog',
                <ConnectModelDialog
                    productID={product.id}
                    additionalCodes={product.additionalCodes.map((c) => {
                        return { code: c.code, salesChannelID: c.salesChannelID, merchantProductName: c.merchantProductName };
                    })}
                    productColors={product.colors as ProductColor[]}
                    merchantProductOptionsConnected={[]}
                    salesChannels={realSalesChannels}
                    productSalesChannelID={product.salesChannel.id}
                    onConfirmMappingVariants={() => {
                        closeDialog('connectProductDialog');
                        if (!showCopyUrl) {
                            showInfoNotification({
                                message: product!.statusID === 1 ? T._('Unpublishing product', 'Admin') : T._('Publishing product', 'Admin')
                            });
                        }
                        setStatusProduct({
                            variables: {
                                input: {
                                    productID: product!.id!,
                                    status: product!.statusID === 1 ? 'UN_PUBLISHED' : 'PUBLISHED'
                                }
                            },
                            onError: () => {
                                showErrorNotification({
                                    message: product!.statusID === 1 ? T._('Failed Unpublish product', 'Admin') : T._('Failed Publish product', 'Admin')
                                });
                            },
                            onCompleted: (response, errors) => {
                                if (!showCopyUrl) {
                                    showSuccessNotification({
                                        message: product!.statusID === 1 ? T._('Product unpublished', 'Admin') : T._('Product published', 'Admin')
                                    });
                                }
                            }
                        });
                    }}
                    onConfirm={async (sourceProduct, salesChannelId) => {
                        if (!product) {
                            showDialog('no-selection', <MessageDialog message={T._('Select a product', 'Admin')} />);
                            return;
                        }

                        if (!sourceProduct) return;

                        closeDialog('connectProductDialog');

                        const loadingNotification = showNotification({
                            type: 'default',
                            message: T._('Publishing your customizable product...', 'Admin')
                        });

                        let response!: testPageConnectModelMutation$data;

                        try {
                            // Save product
                            response = await new Promise((resolve, reject) =>
                                commitConnectModel({
                                    onCompleted: (response) => {
                                        resolve(response);
                                    },
                                    onError: (response) => reject(response),
                                    updater: (store) => {
                                        store.invalidateStore();
                                    },
                                    variables: {
                                        id: model!.id,
                                        target: {
                                            name: sourceProduct!.name,
                                            sku: sourceProduct!.sku,
                                            imageUrl: sourceProduct!.thumbnail,
                                            salesChannelId
                                        }
                                    }
                                })
                            );
                            showNotification({
                                type: 'success',
                                message: T._('Product connected and published!', 'Admin'),
                                dismiss: {
                                    duration: 3000
                                }
                            });

                            refreshAfterPublish();
                        } catch (ex: any) {
                            showNotification({
                                type: 'danger',
                                message: (
                                    <>
                                        {T._('Failed connecting and publishing product.', 'Admin')}
                                        <br />
                                        {ex.errors?.[0].extensions?.message}
                                    </>
                                ),
                                dismiss: {
                                    duration: 3000
                                }
                            });
                        }
                        removeNotification(loadingNotification);
                    }}
                    forPublishingProduct={true}
                />
            );

            return;
        } else if (!salesChannels.some((s) => s.ecommerceSettings?.ecommerceName !== 'NONE')) {
            showDialog(
                'complete-ecommerce-integration',
                <NoIntegrationDialog
                    redirectPage={removeCultureAdmin()}
                    productId={toLocalID(productId) ?? 0}
                    subTitleNoIntegration={T._('Do you want to start selling your customizable products at your store?', 'Admin')}
                    message={
                        <label>{T._('Complete the integration with Zakeke by selecting your ecommerce platform and connecting your store.', 'Admin')}</label>
                    }
                ></NoIntegrationDialog>
            );
            return;
        }

        if (!showCopyUrl) {
            showInfoNotification({ message: product!.statusID === 1 ? T._('Unpublishing product', 'Admin') : T._('Publishing product', 'Admin') });
        }
        setStatusProduct({
            variables: {
                input: {
                    productID: product!.id!,
                    status: product!.statusID === 1 ? 'UN_PUBLISHED' : 'PUBLISHED'
                }
            },
            onError: () => {
                showErrorNotification({ message: product!.statusID === 1 ? T._('Failed Unpublish product', 'Admin') : T._('Failed Publish product', 'Admin') });
            },
            onCompleted: (response, errors) => {
                if (!showCopyUrl) {
                    showSuccessNotification({ message: product!.statusID === 1 ? T._('Product unpublished', 'Admin') : T._('Product published', 'Admin') });
                }
            }
        });

        refreshAfterPublish();
    };

    const refreshAfterPublish = () => {
        const updatedSearchParams = new URLSearchParams(searchParams.toString());
        updatedSearchParams.delete('publish');

        const newSearch = updatedSearchParams.toString();
        const newPath = `${location.pathname}${newSearch ? '?' + newSearch : ''}`;

        startTransition(() => setFetchKey((prev) => prev + 1));
        navigate(newPath, { replace: true });
    };

    function removeCultureAdmin() {
        const { pathname, search } = window.location;
        const pattern = /^\/[^/]+\/Admin(?=\/|$)/i;

        const cleanedPath = pathname.replace(pattern, '/').replace(/\/+/g, '/');
        return `${cleanedPath}${search}`;
    }

    const handleAddSalesChannels = (modelID: string, salesChannelID: string) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        let realSalesChannels = salesChannels.filter((s) => s.ecommerceSettings?.ecommerceName !== 'NONE');

        // if (product.sourceProductTemplateID) {
        if (realSalesChannels.length == 0) {
            showDialog(
                'complete-ecommerce-integration',
                <NoIntegrationDialog
                    subTitleNoIntegration={T._('Ready to start selling customizable products?', 'Admin')}
                    message={
                        <label>
                            {T._('You need to integrate Zakeke with your store in order to publish and start selling your customizable products.', 'Admin')}
                        </label>
                    }
                ></NoIntegrationDialog>
            );
            return;
        } else if (realSalesChannels.length == 1) {
            shopifyAuthEcommerceSettings?.isTestStore
                ? showDialog(
                      'add-sales-channel-not-available',
                      <MessageDialog
                          windowDecorator={SalesChannelNotAvailableDialogWindow}
                          message={
                              <>
                                  {T._(
                                      "We're sorry, but you cannot add sales channels at this time. Your account is currently in trial mode on Shopify or is a development store.",
                                      'Admin'
                                  )}
                                  <br />
                                  <br />
                                  {T._('To access this feature, please upgrade to an active Shopify plan.', 'Admin')}
                                  <br />
                                  <br />
                                  {T._('Thank you for your understanding.', 'Admin')}
                              </>
                          }
                          buttons={[
                              {
                                  label: T._('OK', 'Admin'),
                                  onClick: () => {
                                      closeDialog('add-sales-channel-not-available');
                                  }
                              }
                          ]}
                      />
                  )
                : showDialog(
                      'add-sales-channel',
                      <AddSalesChannelDialog
                          onSalesChannelCreated={() => {
                              closeDialog('add-sales-channel');
                          }}
                      ></AddSalesChannelDialog>
                  );
        } else {
            let availableSalesChannels = realSalesChannels.filter(
                (rs) => rs.id != model.salesChannel.id && !model.additionalCodes.some((ac) => ac.salesChannelID == rs.id)
            );

            if (availableSalesChannels.length == 0) {
                showNotification({
                    type: 'danger',
                    message: T._('The product has been connected with all sales channels.', 'Admin'),
                    dismiss: {
                        duration: 3000
                    }
                });
                return;
            }

            showDialog(
                'connectProductDialog',
                <Suspense
                    fallback={
                        <DialogOverlayConnect>
                            <Loader />
                        </DialogOverlayConnect>
                    }
                >
                    <ConnectModelDialog
                        productID={'guid://Zakeke/' + uuid() + '/' + toLocalID(model.id)}
                        forEditProduct={false}
                        forAddingNewSalesChannel={true}
                        merchantProductOptionsConnected={[]}
                        additionalCodes={model.additionalCodes.map((c) => {
                            return { code: c.code, salesChannelID: c.salesChannelID, merchantProductName: c.merchantProductName ?? '' };
                        })}
                        productColors={model.colors as ProductColor[]}
                        salesChannels={availableSalesChannels}
                        productSalesChannelID={model.salesChannel.id}
                        onConfirm={async (sourceProduct, salesChannelId) => {
                            if (!model) {
                                showDialog('no-selection', <MessageDialog message={T._('Select a product', 'Admin')} />);
                                return;
                            }

                            if (!sourceProduct) return;

                            closeDialog('connectProductDialog');

                            const loadingNotification = showNotification({
                                type: 'default',
                                message: T._('Publishing your customizable product...', 'Admin')
                            });

                            let response!: productListConnectModelMutation$data;

                            try {
                                // Save product
                                response = await new Promise((resolve, reject) =>
                                    commitConnectModel({
                                        onCompleted: (response) => resolve(response),
                                        onError: (response) => reject(response),
                                        updater: (store) => {
                                            store.invalidateStore();
                                        },
                                        variables: {
                                            id: model.id,
                                            target: {
                                                name: sourceProduct!.name,
                                                sku: sourceProduct!.sku,
                                                imageUrl: sourceProduct!.thumbnail,
                                                salesChannelId
                                            }
                                        }
                                    })
                                );

                                showNotification({
                                    type: 'success',
                                    message: T._('Product connected and published!', 'Admin'),
                                    dismiss: {
                                        duration: 3000
                                    }
                                });
                            } catch (ex: any) {
                                showNotification({
                                    type: 'danger',
                                    message: (
                                        <>
                                            {T._('Failed connecting and publishing product.', 'Admin')}
                                            <br />
                                            {ex.errors?.[0].extensions?.message}
                                        </>
                                    ),
                                    dismiss: {
                                        duration: 3000
                                    }
                                });
                            }
                            removeNotification(loadingNotification);
                        }}
                    />
                </Suspense>
            );
        }
    };

    const handleDuplicate = (model: PreviewModel) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        showDialog(
            'duplicate',
            <DuplicateDialog
                sourceProductCode={model!.code}
                salesChannelID={model!.salesChannel.id}
                onConfirm={async (targets) => {
                    if (targets.length === 0) {
                        showDialog('no-selection', <MessageDialog message={T._('Select at last one product.', 'Admin')} />);
                        return;
                    }

                    closeDialog('duplicate');

                    const loadingNotification = showNotification({
                        type: 'default',
                        message: T._('The product is duplicating...', 'Admin')
                    });

                    let response!: testPageDuplicateMutation$data;

                    try {
                        // Save product
                        response = await new Promise<testPageDuplicateMutation$data>((resolve, reject) =>
                            commitDuplicate({
                                onCompleted: (response) => resolve(response),
                                onError: (response) => reject(response),
                                updater: (store) => {
                                    store.invalidateStore();
                                },
                                variables: {
                                    id: model!.id,
                                    targets: targets.map((x) => ({
                                        name: x.name!,
                                        sku: x.sku!,
                                        imageUrl: x.thumbnail,
                                        salesChannelId: model?.salesChannel.id!
                                    }))
                                }
                            })
                        );
                        if (response.duplicateModel.status === 'WARNING') {
                            showWarningNotification({
                                message: T._('There is already a duplicating product request in progress. Please wait for it to complete.', 'Admin')
                            });
                        } else {
                            showNotification({
                                type: 'success',
                                message: T._('Products duplicated!', 'Admin'),
                                dismiss: {
                                    duration: 3000
                                }
                            });
                            window.location.href = backofficeUrl('/products');
                        }
                    } catch (ex: any) {
                        showNotification({
                            type: 'danger',
                            message: (
                                <>
                                    {T._('Failed duplicating products.', 'Admin')}
                                    <br />
                                    {ex.errors?.[0].extensions?.message}
                                </>
                            ),
                            dismiss: {
                                duration: 3000
                            }
                        });
                    }
                    removeNotification(loadingNotification);
                }}
            />
        );
    };
    const handleDeleteClick = (model: PreviewModel) => {
        if (!isAllowed(['PRODUCTS_DELETE'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        showDialog(
            'delete-product',
            <QuestionDialog
                message={stringFormat(T._('Do you want delete the product "{0}"?', 'Admin'), model!.name)}
                onYesClick={() => {
                    closeDialog('delete-product');
                    showInfoNotification({
                        message: T._('Please wait. The product is deleting..', 'Admin'),
                        dismiss: {
                            duration: 5000
                        }
                    });
                    deleteProduct({
                        variables: {
                            productID: model!.id
                        },
                        onError: () => {
                            showErrorNotification({ message: T._('Failed deleting product', 'Admin') });
                        },
                        onCompleted: () => {
                            showSuccessNotification({ message: T._('Product deleted.', 'Admin') });
                            incrementStoreKey();
                            navigate(backofficeUrl('/products'));
                        },
                        updater: (store) => store.invalidateStore()
                    });
                }}
            />
        );
    };

    const handleMarketplacePreviewClick = (productID: string) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        showDialog('marketplace-preview-url', <MarketplacePreviewUrlDialog productID={productID} />);
    };

    const baseIframeUrl = useRef(subscription?.isOverMaxNumTestOrders ? `${model.iframeUrl.testUrl}&testingDisabled=1` : model.iframeUrl.testUrl);
    const prevActiveProductId = useRef(activeProductId);
    const composerIframeRef = useRef<HTMLIFrameElement>(null);
    const isComposer = model.zakekeEnvironment === 'COMPOSER';

    // Update base URL only when the active product changes (not on publish/unpublish refetch)
    if (prevActiveProductId.current !== activeProductId) {
        prevActiveProductId.current = activeProductId;
        baseIframeUrl.current = subscription?.isOverMaxNumTestOrders ? `${model.iframeUrl.testUrl}&testingDisabled=1` : model.iframeUrl.testUrl;
    }

    // Composer products: send postMessage to load the product in the iframe
    useEffect(() => {
        if (!isComposer) return;

        const sendLoadMessage = () => {
            composerIframeRef.current?.contentWindow?.postMessage(
                {
                    type: 'load',
                    parameters: {
                        name: model.name,
                        token: config.token,
                        ecommerce: 'api',
                        integrationVersion: 2,
                        currency: 'USD',
                        culture: config.culture || 'en',
                        modelCode: model.code,
                        qty: 1,
                        attributes: []
                    }
                },
                '*'
            );
        };

        const interval = setInterval(sendLoadMessage, 500);

        const handleMessage = (event: MessageEvent) => {
            if (event.data?.zakekeType === 'loaded') {
                clearInterval(interval);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => {
            clearInterval(interval);
            window.removeEventListener('message', handleMessage);
        };
    }, [isComposer, model.name, model.code]);

    const selectedPrintingId = discoverySelections.printing;
    const getAQuoteEnabled = discoverySelections.tools === 'get-a-quote';

    const iframeSrc = useMemo(() => {
        const base = baseIframeUrl.current;
        if (!base) return base;
        if (!selectedPrintingId && !getAQuoteEnabled) return base;

        try {
            const url = new URL(base);
            if (selectedPrintingId) {
                url.searchParams.set('printtype', selectedPrintingId);
            }
            if (getAQuoteEnabled) {
                url.searchParams.set('getaquote', '1');
            }
            return url.toString();
        } catch {
            let result = base;
            if (selectedPrintingId) {
                const separator = result.includes('?') ? '&' : '?';
                result = `${result}${separator}printtype=${selectedPrintingId}`;
            }
            if (getAQuoteEnabled) {
                const separator = result.includes('?') ? '&' : '?';
                result = `${result}${separator}getaquote=1`;
            }
            return result;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeProductId, selectedPrintingId, getAQuoteEnabled]);

    return (
        <FullscreenOverlay>
            <TopBar $discovery={discoveryMode}>
                <TopBarLeft>
                    <ZTooltip position='bottom' title={T._('Switch product', 'Admin')} enterDelay={0} exitDelay={0} interactive={false}>
                        <ChangeProductButton
                            $discovery={discoveryMode}
                            onClick={() => {
                                showDialog(
                                    'product-catalog',
                                    <ProductCatalogDialog
                                        onConfirm={(modelID) => {
                                            closeDialog('product-catalog');
                                            const newProductId = String(modelID);
                                            startTransition(() => {
                                                setActiveProductId(newProductId);
                                            });
                                            navigate(backofficeUrl(`/test-mode/${newProductId}`), { replace: true });
                                        }}
                                    />
                                );
                            }}
                        >
                            <Icon>
                                <ChangeProductIcon />
                            </Icon>
                        </ChangeProductButton>
                    </ZTooltip>
                    <ProductName $discovery={discoveryMode}>{model.name}</ProductName>
                    {!discoveryMode ? (
                        <>
                            <BadgeContainer>
                                <StyledBadge color={theme.app.primary} badgeColor={model.statusID === 1 ? '#d6f3e2' : '#dde6fe'} showCircle={false}>
                                    {model.statusID === 1 ? T._('Online', 'Admin') : T._('Not published', 'Admin')}
                                </StyledBadge>
                            </BadgeContainer>
                            <PublishSwitchWrapper>
                                <ZSwitch checked={isPublished} onChange={() => handleSetStatusClick(model)} disabled={!isAllowed(['PRODUCTS_PUBLISH'])} />
                                <PublishSwitchLabel>{isPublished ? T._('Published', 'Admin') : T._('Unpublished', 'Admin')}</PublishSwitchLabel>
                            </PublishSwitchWrapper>
                            {isPublished && model.salesChannel.ecommerceSettings?.definitiveUrl && (
                                <Link href={model.salesChannel.ecommerceSettings.definitiveUrl} target='_blank'>
                                    <Icon>
                                        <ExternalLinkIcon />
                                    </Icon>
                                </Link>
                            )}
                        </>
                    ) : null}
                </TopBarLeft>
                <TopBarCenter>
                    {!isOldUserInterface && (
                        <>
                            <ZTooltip position='bottom' title={T._('Desktop', 'Admin')}>
                                <PreviewSizeButton
                                    $active={previewSize === 'desktop-wide'}
                                    $discovery={discoveryMode}
                                    onClick={() => setPreviewSize('desktop-wide')}
                                >
                                    <Icon>
                                        <DesktopWideIcon />
                                    </Icon>
                                </PreviewSizeButton>
                            </ZTooltip>
                            <ZTooltip position='bottom' title={T._('Laptop', 'Admin')}>
                                <PreviewSizeButton $active={previewSize === 'desktop'} $discovery={discoveryMode} onClick={() => setPreviewSize('desktop')}>
                                    <Icon>
                                        <DesktopIcon />
                                    </Icon>
                                </PreviewSizeButton>
                            </ZTooltip>
                            <ZTooltip position='bottom' title={T._('Mobile', 'Admin')}>
                                <PreviewSizeButton $active={previewSize === 'mobile'} $discovery={discoveryMode} onClick={() => setPreviewSize('mobile')}>
                                    <Icon>
                                        <MobileIcon />
                                    </Icon>
                                </PreviewSizeButton>
                            </ZTooltip>
                        </>
                    )}
                </TopBarCenter>
                <TopBarRight>
                    {!discoveryMode && (
                        <SelectActions
                            header={
                                <Icon $hoverable>
                                    <VerticalThreePoint />
                                </Icon>
                            }
                            items={[
                                <ButtonActions
                                    onClick={() => {
                                        if (isComposer) {
                                            window.location.href = `/Composer?id=${toLocalID(model!.id)}&page=product`;
                                        } else {
                                            navigate(backofficeUrl('/Product?product_id=' + toLocalID(model!.id)));
                                        }
                                    }}
                                >
                                    {T._('Edit Product', 'Admin')}
                                </ButtonActions>,
                                model.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' &&
                                    model.provider !== 'INVITION' &&
                                    model.provider !== 'CUSTOM_CAT' &&
                                    model.provider !== 'PRINTFUL' && (
                                        <ButtonActions onClick={() => handleDuplicate(model)}>{T._('Duplicate', 'Admin')}</ButtonActions>
                                    ),
                                ...(isComposer
                                    ? []
                                    : [
                                          model.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' && model.canAddAdditionalCode && (
                                              <ButtonActions onClick={() => handleAddSalesChannels(model.id, model.salesChannel.id)}>
                                                  {T._('Add to a Sales Channel', 'Admin')}
                                              </ButtonActions>
                                          ),
                                          (model.salesChannel.ecommerceSettings?.ecommerceName === 'ETSY' ||
                                              model.additionalCodes.some((c) => c.salesChannel.ecommerceSettings?.ecommerceName === 'ETSY')) && (
                                              <ButtonActions onClick={() => handleMarketplacePreviewClick(model!.id)} disabled={model.statusID !== 1}>
                                                  {T._('Generate a shareable demo product URL', 'Admin')}
                                              </ButtonActions>
                                          ),
                                          <MenuDivider />,
                                          <ButtonActions onClick={() => navigate(backofficeUrl('/printing-methods'))}>
                                              {T._('Set a Printing Method', 'Admin')}
                                          </ButtonActions>,
                                          <ButtonActions onClick={() => navigate(backofficeUrl('/pricings'))}>
                                              {T._('Add a Pricing Rule', 'Admin')}
                                          </ButtonActions>,
                                          <ButtonActions onClick={() => navigate(backofficeUrl('/3Dpreview-and-AR/?product_id=' + toLocalID(model!.id)))}>
                                              {T._('Activate the 3D preview', 'Admin')}
                                          </ButtonActions>,
                                          <ButtonActions onClick={() => navigate(backofficeUrl('/tools'))}>{T._('Explore Tools', 'Admin')}</ButtonActions>,
                                          <ButtonActions onClick={() => navigate(backofficeUrl('/ai-creative-tools'))}>
                                              {T._('Explore AI Creative Tools', 'Admin')}
                                          </ButtonActions>
                                      ]),
                                <MenuDivider />,
                                <ButtonActionsDelete onClick={() => handleDeleteClick(model)}>{T._('Delete', 'Admin')}</ButtonActionsDelete>
                            ]}
                            orientation={'RIGHT'}
                        />
                    )}
                    {discoveryMode && (
                        <>
                            <UISettingsButton onClick={() => navigate(backofficeUrl('/settings/UserInterface'))}>
                                {T._('User Interface Settings', 'Admin')}
                            </UISettingsButton>
                            <UISettingsButton onClick={() => navigate(backofficeUrl('/User/?tab=subscriptions#addons-title'))}>
                                {T._("Remove 'Powered by Zakeke'", 'Admin')}
                            </UISettingsButton>
                        </>
                    )}
                    <CloseButton onClick={() => navigate(-1)} aria-label={T._('Close', 'Admin')}>
                        <Icon>
                            <CloseIcon />
                        </Icon>
                    </CloseButton>
                </TopBarRight>
            </TopBar>

            {!discoveryMode && !isComposer && isInfoBannerVisible && (
                <BannerSection>
                    <NotificationBanner
                        type='INFO'
                        showCloseButton={true}
                        onClose={() => setIsInfoBannerVisible(false)}
                        content={
                            <span>
                                {T._('This preview shows what your customers will see. Test the customization flow and improve it by configuring a', 'Admin')}{' '}
                                <Link onClick={() => navigate(backofficeUrl('/printing-methods'))}>{T._('Printing Method', 'Admin')}</Link>
                                {T._(', using advanced', 'Admin')} <Link onClick={() => navigate(backofficeUrl('/tools'))}>{T._('Tools', 'Admin')}</Link>
                                {T._(', or leveraging', 'Admin')}{' '}
                                <Link onClick={() => navigate(backofficeUrl('/ai-creative-tools'))}>{T._('AI Creative Tools', 'Admin')}</Link>{' '}
                                {T._('to enhance the personalization experience.', 'Admin')}
                            </span>
                        }
                    />
                </BannerSection>
            )}

            {discoveryMode && (
                <DiscoveryModePanel
                    selections={discoverySelections}
                    onSelect={handleDiscoverySelect}
                    onSave={(category, item) => {
                        const routes: Record<string, string> = {
                            printing: `/printing-method?from=discovery&template=${item.id}`,
                            pricing: `/pricings?from=discovery&template=${item.id}`,
                            ai: `/ai?from=discovery&tool=${item.id}`,
                            tools: `/tools?from=discovery&tool=${item.id}`
                        };
                        navigate(backofficeUrl(routes[category] ?? '/'));
                    }}
                    onBuildYourOwn={(category) => {
                        const routes: Record<string, string> = {
                            printing: '/printing-method',
                            pricing: '/pricings',
                            ai: '/ai-creative-tools',
                            tools: '/tools'
                        };
                        navigate(backofficeUrl(routes[category] ?? '/'));
                    }}
                />
            )}

            <IframeSection>
                <IframeContainer ref={previewContainerRef}>
                    <WaitForProductExists
                        productId={model.id}
                        fallback={
                            <Center>
                                <Loader />
                            </Center>
                        }
                    >
                        {previewSize === 'desktop-wide' ? (
                            <StyledIframe
                                ref={isComposer ? composerIframeRef : undefined}
                                key={`${iframeSrc}-${previewSize}`}
                                src={iframeSrc}
                                allow='camera; microphone; autoplay; fullscreen; picture-in-picture'
                                allowFullScreen
                            />
                        ) : (
                            <PreviewSkin $width={skinConfig.width} $height={skinConfig.height} $scale={skinConfig.scale} $skin={skinConfig.skin}>
                                <StyledIframe
                                    ref={isComposer ? composerIframeRef : undefined}
                                    key={`${iframeSrc}-${previewSize}`}
                                    src={iframeSrc}
                                    allow='camera; microphone; autoplay; fullscreen; picture-in-picture'
                                    allowFullScreen
                                />
                            </PreviewSkin>
                        )}
                    </WaitForProductExists>
                </IframeContainer>
            </IframeSection>

            {isDiscoveryModeEnabled && (
                <BottomBar>
                    <ModeToggleContainer>
                        <ModeToggleButton
                            $active={!discoveryMode}
                            $variant='test'
                            onClick={() => {
                                setDiscoveryMode(false);
                                setDiscoverySelections({ printing: null, pricing: null, ai: null, tools: null });
                            }}
                        >
                            {T._('Test Mode', 'Admin')}
                        </ModeToggleButton>
                        <ModeToggleButton $active={discoveryMode} $variant='discovery' onClick={() => setDiscoveryMode(true)}>
                            {T._('Discovery Mode', 'Admin')}
                        </ModeToggleButton>
                    </ModeToggleContainer>
                </BottomBar>
            )}
        </FullscreenOverlay>
    );
};

export default TestPage;
